import React, { useState } from "react";
import { C, IMGS, FAQS, SERVICES } from "../constants";
import { Reveal, Eyebrow, H, P, Btn, Sec } from "../components/UI";

function LabelRow({ children, label, req }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
      <label style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: C.gray }}>
        {label}{req && <span style={{ color: C.accent }}> *</span>}
      </label>
      {children}
    </div>
  );
}

function FaqItem({ f, i }) {
  const [open, setOpen] = useState(false);
  return (
    <Reveal delay={i * 70}>
      <div style={{ borderBottom: `1px solid ${C.border}` }}>
        <button
          onClick={() => setOpen(!open)}
          style={{ width: "100%", background: "none", border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "22px 0", gap: 16 }}
        >
          <span style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 15, color: C.black, textAlign: "left" }}>{f.q}</span>
          <span style={{ width: 26, height: 26, borderRadius: "50%", background: open ? C.accent : C.surface, border: `1px solid ${open ? C.accent : C.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontWeight: 900, fontSize: 15, color: open ? C.white : C.muted, transition: "all 0.22s", transform: open ? "rotate(45deg)" : "none" }}>+</span>
        </button>
        {open && <div style={{ paddingBottom: 22 }}><P size="sm">{f.a}</P></div>}
      </div>
    </Reveal>
  );
}

export default function ContactPage() {
  const [form, setForm]   = useState({ name: "", company: "", email: "", phone: "", service: "", budget: "", message: "" });
  const [focus, setFocus] = useState("");
  const [sent, setSent]   = useState(false);

  const h = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const inputStyle = (name) => ({
    fontFamily: "'Poppins', sans-serif", fontSize: 15, color: C.black,
    background: C.white, border: "none",
    borderBottom: `2px solid ${focus === name ? C.accent : C.border}`,
    padding: "11px 0", outline: "none", width: "100%",
    transition: "border-color 0.22s",
  });

  const selectStyle = {
    ...inputStyle("select"),
    appearance: "none", cursor: "pointer",
    color: form.service ? C.black : C.muted,
  };

  return (
    <div style={{ paddingTop: 84 }}>
      {/* Hero */}
      <section style={{ position: "relative", overflow: "hidden", minHeight: 440, display: "flex", alignItems: "flex-end", background: C.black }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${IMGS.contact})`, backgroundSize: "cover", backgroundPosition: "center", opacity: 0.38 }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(10,10,10,1) 26%, rgba(10,10,10,0.38))" }} />
        <div style={{ position: "relative", padding: "0 clamp(20px,7vw,120px) 80px", width: "100%" }}>
          <Reveal>
            <Eyebrow text="Get in Touch" />
            <H size="xl" color={C.white} style={{ marginTop: 12 }}>Let's Build Something<br />Extraordinary Together</H>
          </Reveal>
        </div>
      </section>

      {/* Info strip */}
      <div style={{ background: C.accent, padding: "0 clamp(20px,7vw,120px)" }}>
        <div style={{ display: "flex", flexWrap: "wrap" }}>
          {[
            { l: "Email",         v: "hello@tharagaiproduction.com" },
            { l: "Location",      v: "Chennai, Tamil Nadu, India"   },
            { l: "Response Time", v: "Within 24 Hours"              },
          ].map((c, i) => (
            <div key={c.l} style={{ padding: "22px 40px 22px 0", marginRight: 40, borderRight: i < 2 ? "1px solid rgba(255,255,255,0.18)" : "none" }}>
              <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 9, letterSpacing: 3, color: "rgba(255,255,255,0.55)", textTransform: "uppercase", marginBottom: 5 }}>{c.l}</div>
              <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 15, color: C.white, fontWeight: 500 }}>{c.v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Form section */}
      <Sec bg={C.surface}>
        <div className="tp-split" style={{ gap: 72, alignItems: "start" }}>
          {/* Left info */}
          <Reveal from="left">
            <Eyebrow text="Start Here" />
            <H size="lg" style={{ marginTop: 12, marginBottom: 24 }}>Tell Us About<br />Your Project</H>
            <P style={{ marginBottom: 36 }}>
              Every great production starts with a simple conversation. Fill in your details and tell us what you're looking to create — we'll respond within 24 hours with a tailored proposal.
            </P>
            <div style={{ background: C.white, border: `1px solid ${C.border}`, padding: "26px 26px 30px" }}>
              <h4 style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: 14, color: C.black, marginBottom: 18 }}>What Happens Next</h4>
              {["We review your brief carefully", "Our team sends a tailored proposal", "We schedule a discovery call", "Production begins"].map((step, i) => (
                <div key={step} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: i < 3 ? 13 : 0 }}>
                  <span style={{ width: 22, height: 22, background: C.accentLight, border: `1px solid ${C.accent}`, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontFamily: "'Montserrat', sans-serif", fontWeight: 900, fontSize: 10, color: C.accent }}>{i + 1}</span>
                  <P size="sm" color={C.charcoal}>{step}</P>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Form */}
          <Reveal from="right">
            {sent ? (
              <div style={{ background: C.white, border: `1px solid ${C.border}`, padding: "72px 36px", textAlign: "center" }}>
                <div style={{ width: 64, height: 64, background: C.accentLight, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", fontSize: 28, color: C.accent }}>✓</div>
                <H size="md" style={{ marginBottom: 14 }}>Message Received!</H>
                <P color={C.muted} style={{ maxWidth: 340, margin: "0 auto" }}>
                  Thank you for reaching out. We'll respond with a tailored proposal within 24 hours.
                </P>
              </div>
            ) : (
              <div style={{ background: C.white, border: `1px solid ${C.border}`, padding: "44px 40px" }}>
                <h3 style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: 21, color: C.black, marginBottom: 32 }}>Send Your Brief</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                  <LabelRow label="Your Name" req>
                    <input name="name" value={form.name} onChange={h} placeholder="Priya Sharma" style={inputStyle("name")} onFocus={() => setFocus("name")} onBlur={() => setFocus("")} />
                  </LabelRow>
                  <div className="tp-form2">
                    <LabelRow label="Email Address" req>
                      <input name="email" type="email" value={form.email} onChange={h} placeholder="hello@brand.com" style={inputStyle("email")} onFocus={() => setFocus("email")} onBlur={() => setFocus("")} />
                    </LabelRow>
                    <LabelRow label="Phone">
                      <input name="phone" type="tel" value={form.phone} onChange={h} placeholder="+91 98765 43210" style={inputStyle("phone")} onFocus={() => setFocus("phone")} onBlur={() => setFocus("")} />
                    </LabelRow>
                  </div>
                  <div className="tp-form2">
                    <LabelRow label="Service Needed">
                      <select name="service" value={form.service} onChange={h} style={selectStyle} onFocus={() => setFocus("service")} onBlur={() => setFocus("")}>
                        <option value="">Select a service</option>
                        {SERVICES.map((s) => <option key={s.id}>{s.title}</option>)}
                        <option>Branding & Copywriting</option>
                        <option>Food / Product Shoot</option>
                        <option>Fashion Film</option>
                        <option>Other</option>
                      </select>
                    </LabelRow>
                    <LabelRow label="Budget Range">
                      <select name="budget" value={form.budget} onChange={h} style={{ ...selectStyle, borderBottomColor: focus === "budget" ? C.accent : C.border, color: form.budget ? C.black : C.muted }} onFocus={() => setFocus("budget")} onBlur={() => setFocus("")}>
                        <option value="">Select budget</option>
                        <option>Under ₹25,000</option>
                        <option>₹25,000 – ₹75,000</option>
                        <option>₹75,000 – ₹2,00,000</option>
                        <option>₹2,00,000 – ₹5,00,000</option>
                        <option>₹5,00,000+</option>
                      </select>
                    </LabelRow>
                  </div>
                  <LabelRow label="Your Project Brief" req>
                    <textarea
                      name="message" value={form.message} onChange={h}
                      placeholder="Tell us about your brand, goals, timeline, and any reference content you love..."
                      rows={5}
                      style={{ fontFamily: "'Poppins', sans-serif", fontSize: 15, color: C.black, background: C.surface, border: `1px solid ${focus === "message" ? C.accent : C.border}`, padding: "12px 14px", outline: "none", resize: "vertical", transition: "border-color 0.22s", width: "100%" }}
                      onFocus={() => setFocus("message")} onBlur={() => setFocus("")}
                    />
                  </LabelRow>
                  <Btn variant="solid" onClick={() => { if (form.name && form.email && form.message) setSent(true); }} style={{ width: "100%", justifyContent: "center", padding: "15px 0", fontSize: 13 }}>
                    Send Your Brief
                  </Btn>
                  <P size="sm" color={C.muted}>* Required fields. We respond within 24 hours. Your info is never shared.</P>
                </div>
              </div>
            )}
          </Reveal>
        </div>
      </Sec>

      {/* FAQ */}
      <Sec bg={C.white}>
        <Reveal>
          <Eyebrow text="FAQs" />
          <H size="lg" style={{ marginBottom: 52 }}>Frequently Asked Questions</H>
        </Reveal>
        <div className="tp-split" style={{ gap: "0 72px", alignItems: "start" }}>
          <div>{FAQS.slice(0, 3).map((f, i) => <FaqItem key={f.q} f={f} i={i} />)}</div>
          <div>{FAQS.slice(3).map((f, i) => <FaqItem key={f.q} f={f} i={i} />)}</div>
        </div>
      </Sec>

      {/* Closing */}
      <section style={{ background: C.black, padding: "96px clamp(20px,7vw,120px)" }}>
        <div className="tp-split" style={{ gap: 64, alignItems: "center" }}>
          <Reveal from="left">
            <H size="lg" color={C.white} style={{ marginBottom: 22 }}>Follow Our<br />Creative Journey</H>
            <P color="rgba(255,255,255,0.48)" style={{ marginBottom: 32 }}>
              Stay updated with our latest projects, behind-the-scenes moments, and creative insights across our social platforms.
            </P>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {["Instagram", "YouTube", "LinkedIn", "Facebook"].map((s) => (
                <span key={s}
                  style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 600, fontSize: 11, color: "rgba(255,255,255,0.4)", border: "1px solid rgba(255,255,255,0.1)", padding: "9px 18px", letterSpacing: 1, cursor: "pointer", transition: "all 0.22s", textTransform: "uppercase" }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = C.accent; e.currentTarget.style.borderColor = C.accent; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.4)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; }}
                >{s}</span>
              ))}
            </div>
          </Reveal>
          <Reveal from="right">
            <div style={{ background: C.accent, padding: "44px 36px" }}>
              <H size="md" color={C.white} style={{ marginBottom: 14 }}>Have an Urgent Project?</H>
              <P color="rgba(255,255,255,0.78)" style={{ marginBottom: 24 }}>
                We accommodate rush timelines for the right projects. Reach out directly and we'll see how we can help.
              </P>
              <div style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: 17, color: C.white }}>
                hello@tharagaiproduction.com
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
